// hooks/auth/useRecoveryPhoneOtp.ts
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  selectAuthStatus,
  selectAuthError,
  selectRecoveryMaskedPhone,
  selectRecoveryToken,
} from '@/store/selectors/auth';
import { verifyRecoveryTwoFactorThunk } from '@/store/thunks/auth/verifyRecoveryTwoFactorThunk';
import { sendRecoveryTwoFactorThunk } from '@/store/thunks/auth/sendRecoveryTwoFactorThunk';
import { clearLoginError } from '@/store/slices/auth/authSlice';
import { MIN_LEN, OTP_LENGTH } from '@/constants';

interface UseRecoveryPhoneOtpState {
  otp: string;
  isLoading: boolean;
  globalError: string | null;
  maskedPhoneLabel: string;
}

interface UseRecoveryPhoneOtpHandlers {
  handleOtpChange: (newOtp: string) => void;
  handleSubmit: (event?: FormEvent) => void;
  handleResend: () => void;
  handleBack: () => void;
}

interface UseRecoveryPhoneOtpReturn {
  state: UseRecoveryPhoneOtpState;
  handlers: UseRecoveryPhoneOtpHandlers;
}

/**
 * Hook for /reset-password/two-factor-verify route
 * Handles phone 2FA OTP verification during password recovery
 */
export function useRecoveryPhoneOtp(): UseRecoveryPhoneOtpReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const authStatus = useAppSelector(selectAuthStatus);
  const error = useAppSelector(selectAuthError);
  const maskedPhone = useAppSelector(selectRecoveryMaskedPhone);
  const recoveryToken = useAppSelector(selectRecoveryToken);
  const isLoading = authStatus === 'loading';

  const [otp, setOtp] = useState('');

  useEffect(() => {
    dispatch(clearLoginError());
  }, [dispatch]);

  const verify = useCallback(
    async(code: string) => {
      if (code.trim().length !== OTP_LENGTH) return;

      const action = await dispatch(verifyRecoveryTwoFactorThunk({ code }));
      if (verifyRecoveryTwoFactorThunk.fulfilled.match(action)) {
        await navigate({ to: '/reset-password/set-password' });
      }
    },
    [dispatch, navigate],
  );

  const handleOtpChange = useCallback(
    (newOtp: string): void => {
      setOtp(newOtp);
      if (newOtp.length === OTP_LENGTH) {
        void verify(newOtp);
      }
    },
    [verify],
  );

  const handleSubmit = useCallback(
    (event?: FormEvent): void => {
      if (event !== undefined) event.preventDefault();
      if (otp.length === OTP_LENGTH) {
        void verify(otp);
      }
    },
    [otp, verify],
  );

  const handleResend = useCallback((): void => {
    if (typeof recoveryToken !== 'string' || recoveryToken.trim().length === MIN_LEN) return;
    setOtp('');
    void dispatch(sendRecoveryTwoFactorThunk({ recoveryToken }));
  }, [dispatch, recoveryToken]);

  const handleBack = useCallback((): void => {
    void navigate({ to: '/reset-password/otp-verify' });
  }, [navigate]);

  const globalError =
    typeof error === 'string' && error.length > MIN_LEN ? error : null;

  const maskedPhoneLabel =
    typeof maskedPhone === 'string' && maskedPhone.trim().length > MIN_LEN
      ? maskedPhone
      : 'your phone number';

  return {
    state: { otp, isLoading, globalError, maskedPhoneLabel },
    handlers: { handleOtpChange, handleSubmit, handleResend, handleBack },
  };
}
